import { useState, useEffect } from 'react';

import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { FormikProps, FormikProvider } from 'formik';

import { getFirstCard } from './utils';
import { SavedCard, MixpanelEvents } from '../../../../contracts';
import { InputField } from '../../../InputField/InputField';
import { usePaymentState } from '../../hooks/use-payment-state';
import { useAppConfig } from '../../../../hooks/use-app-config';
import { useCheckout } from '../../../../hooks/use-checkout';
import { SavedCreditCard } from './components/SavedCard/SavedCard';
import { NewCardButton } from './components/NewCardButton/NewCardButton';
import { EMAIL_FIELD_DEFAULT_ATTRIBUTES } from '../../../../constants/consts';
import { sendMixpanelEvent } from '../../../../utils/mixpanel-utils';

import './styles.scss';
interface SavedCardsProps {
  cards: SavedCard[];
  isSecurityCodeRequired?: boolean;
  formik: FormikProps<SavedCardFormProps>;
  shouldHideEmailField?: boolean;
  onAddNewCardClicked: () => void;
  onCardRemoveClicked: (creditCard: SavedCard) => void;
}

export interface SavedCardFormProps {
  brand?: string;
  email: string;
  cardID: string;
  securityCode: string;
  pspCustomerId?: string;
}

export function SavedCards({
  cards,
  formik,
  onAddNewCardClicked,
  onCardRemoveClicked,
  isSecurityCodeRequired,
  shouldHideEmailField,
}: SavedCardsProps) {
  const [selectedCardId, setSelectedCardId] = useState('');
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const { getMixpanelEventParams } = useCheckout();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { featureFlags } = useAppConfig();
  const shouldHideFieldsLabels = featureFlags?.checkout_remove_field_labels;

  useEffect(() => {
    const isSelectedCardExists = cards.some((card) => card.pspCardIdentifier === selectedCardId);
    if (cards.length > 0 && !isSelectedCardExists) {
      const firstCard = getFirstCard(cards);
      if (firstCard) {
        handleCreditCardSelected(firstCard);
      }
    }
  }, [cards]);

  const handleCreditCardSelected = (creditCard: SavedCard) => {
    const newSelectedCardId = creditCard.pspCardIdentifier;
    if (selectedCardId !== newSelectedCardId) {
      setErrorMessage('');
      formik.setFieldValue('securityCode', '');
      formik.setFieldValue('cardID', newSelectedCardId, true);
      formik.setFieldValue('brand', creditCard.brand, false);
      formik.setFieldTouched('securityCode', false);
      formik.setFieldValue('pspCustomerId', creditCard.pspCustomerId || '', false);
      setSelectedCardId(newSelectedCardId);

      // Send Mixpanel event for saved card selection
      sendMixpanelEvent(MixpanelEvents.SAVED_CARD_SELECTED, {
        ...getMixpanelEventParams(),
      });
    }
  };

  return (
    <FormikProvider value={formik}>
      <Stack className="saved-credit-cards-container" spacing={1.5}>
        {!shouldHideEmailField && (
          <InputField
            name="email"
            label={shouldHideFieldsLabels ? '' : t('email')}
            inputProps={{
              placeholder: t('emailAddress'),
              ...EMAIL_FIELD_DEFAULT_ATTRIBUTES,
            }}
          />
        )}
        {cards.map((card) => (
          <SavedCreditCard
            key={card.pspCardIdentifier}
            creditCard={card}
            isSecurityCodeRequired={isSecurityCodeRequired}
            isSelected={selectedCardId === card.pspCardIdentifier}
            onCardRemoved={onCardRemoveClicked}
            onCardSelect={() => handleCreditCardSelected(card)}
          />
        ))}
        <NewCardButton onClick={onAddNewCardClicked} />
      </Stack>
    </FormikProvider>
  );
}
